import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';

export type SignUpModalVariants =
  | 'generic'
  | 'generic_time_delayed'
  | 'follow_athlete'
  | 'join_challenge'
  | 'kudos_comment';

export type SignUpModalContextState = {
  openSignUpModal: ({
    variant,
    params
  }: {
    variant: string;
    params?: Record<string, string>;
  }) => void;
  isSignUpModalOpen: boolean;
  dismissSignUpModal: () => void;
  variant: SignUpModalVariants;
  copyParams: Record<SignUpModalVariants, Record<string, string>>;
};

const SignUpModalContext = createContext<SignUpModalContextState | undefined>(
  undefined
);
SignUpModalContext.displayName = 'SignUpModalContext';

export const useSignUpModalContext = () => {
  const context = useContext(SignUpModalContext);

  // Fail fast if hook is used outside the provider
  if (!context) {
    throw new Error(
      'useSignUpModalContext must be used within the SignUpModalProvider'
    );
  }

  return context;
};

type SignUpModalContextProviderProps = {
  children: ReactNode;
  nameForFollowAthleteVariant: string;
};

export const SignUpModalContextProvider = ({
  children,
  nameForFollowAthleteVariant
}: SignUpModalContextProviderProps) => {
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const [variant, setVariant] = useState<SignUpModalVariants>('generic');

  const openSignUpModal = useCallback(({ variant: _variant }) => {
    setIsSignUpModalOpen(true);
    setVariant(_variant);
  }, []);

  const providerState = useMemo(
    () => ({
      openSignUpModal,
      dismissSignUpModal: () => setIsSignUpModalOpen(false),
      isSignUpModalOpen,
      variant,
      copyParams: {
        generic: {},
        generic_time_delayed: {},
        follow_athlete: { name: nameForFollowAthleteVariant },
        join_challenge: {},
        kudos_comment: {}
      }
    }),
    [openSignUpModal, isSignUpModalOpen, variant, nameForFollowAthleteVariant]
  );

  return (
    <SignUpModalContext.Provider value={providerState}>
      {children}
    </SignUpModalContext.Provider>
  );
};
